import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { noop } from "lodash";
import { FC } from "react";
import { useHistory } from "react-router";

import { Add, OpenInNew } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  Icon,
  Simple,
  SpacingToken,
  Tab,
  TabsList,
  TabsWrapper,
  Tooltip,
} from "@livechat/design-system-react-components";

import { AgentsDropdown } from "components/agents-dropdown";
import { SearchBar } from "components/form";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { GLOBAL_ACCOUNT_URLS } from "constants/globalAccounts";
import { withVar } from "themes/helpers";

import { AgentsRoutes } from "./constants";

type SelectedTab = "agents" | "teams";

export interface IProps {
  agentsCount: number;
  teamsCount: number;
  filterQuery: string;
  canAddAgent: boolean;
  canAddTeam: boolean;
  areAgentsSaved: boolean;
  selectedTab: SelectedTab;
  isMobile: boolean;
  isDropdownVisible: boolean;
  onDropdownToggle(): void;
  onAddAgentSelect(): void;
  onFilterQueryChange(filterQuery: string): void;
  isOwner: boolean;
}

const TopWrapper: FC<IProps> = ({
  agentsCount,
  teamsCount,
  filterQuery,
  canAddAgent,
  canAddTeam,
  selectedTab,
  isMobile,
  onFilterQueryChange,
  isDropdownVisible,
  onDropdownToggle,
  onAddAgentSelect,
  isOwner,
}) => {
  const history = useHistory();
  const agentsDropdownEnabled = canAddAgent || canAddTeam;

  return (
    <StyledTopWrapper>
      <TabsContainer>
        <TabsWrapper>
          <TabsList>
            <Tab
              key="agents"
              count={agentsCount}
              isSelected={selectedTab === "agents"}
              onClick={() => history.push(AgentsRoutes.Agents)}
            >
              Agents
            </Tab>
            <Tab
              key="teams"
              count={teamsCount}
              isSelected={selectedTab === "teams"}
              onClick={() => history.push(AgentsRoutes.Teams)}
            >
              Teams
            </Tab>
          </TabsList>
        </TabsWrapper>
      </TabsContainer>

      <SearchBarContainer>
        <SearchBar
          value={filterQuery}
          onChange={onFilterQueryChange}
          placeholder={`Find ${selectedTab === "agents" ? "agent" : "team"}…`}
          forceOpen={isMobile}
        />
      </SearchBarContainer>

      <Flex>
        {isOwner && (
          <Block marginRight={withVar(SpacingToken.Spacing2)}>
            <Tooltip
              css={tooltipStyles}
              theme="invert"
              triggerRenderer={() => (
                <ButtonHiddenOnMobile
                  size="compact"
                  kind="plain"
                  iconPosition="right"
                  icon={<Icon source={OpenInNew} />}
                  href={GLOBAL_ACCOUNT_URLS.PANEL_INVITATIONS}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Invite settings
                </ButtonHiddenOnMobile>
              )}
            >
              <Simple text="Manage invite settings in Text Accounts" />
            </Tooltip>
          </Block>
        )}
        <Block marginRight="8px">
          <AgentsDropdown
            isVisible={isDropdownVisible}
            onHide={onDropdownToggle}
            canAddAgent={canAddAgent}
            canAddTeam={canAddTeam}
            onAddAgentSelect={onAddAgentSelect}
          >
            <ButtonHiddenOnMobile
              data-testid="add-agents-button"
              size="compact"
              kind="primary"
              icon={<Icon source={Add} />}
              onClick={agentsDropdownEnabled ? onDropdownToggle : noop}
              disabled={!agentsDropdownEnabled}
            >
              Add
            </ButtonHiddenOnMobile>
          </AgentsDropdown>
        </Block>
      </Flex>
    </StyledTopWrapper>
  );
};

const StyledTopWrapper = styled.div`
  display: flex;
  align-items: center;
  border-bottom: solid 1px ${withVar(DesignToken.BorderBasicSecondary)};
  min-height: 51px;

  @media (max-width: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.theme.mobileBreakpoint
    }) {
    flex-direction: column;
  }
`;

const TabsContainer = styled.div`
  margin-right: auto;
  box-sizing: border-box;
  padding: 0 20px;
  align-self: flex-end;
  @media (max-width: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.theme.mobileBreakpoint
    }) {
    order: 1;
    width: 100%;
  }
`;

const SearchBarContainer = styled.div`
  position: relative;
  box-sizing: border-box;
  padding: 5px;

  @media (max-width: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.theme.mobileBreakpoint
    }) {
    width: 100%;
  }
`;

const ButtonHiddenOnMobile = styled(Button)`
  @media (max-width: ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => props.theme.mobileBreakpoint
    }) {
    display: none;
  }
`;

const tooltipStyles = css`
  div > div:first-of-type {
    margin-top: 0;
  }
  img {
    width: 48px;
    height: 48px;
  }
  div > div > button:first-of-type {
    display: none;
  }
  button:nth-of-type(2) {
    margin-left: 0;
  }
`;

export default TopWrapper;
